import React, { useEffect, useState } from 'react';
import api from '../../services/api';
import LoadingSpinner from '../common/LoadingSpinner';
import { getTimeZoneOptions } from '../../utils/timezone';

/*
 * ============================================================
 * Per-provider config fields
 * ------------------------------------------------------------
 * timeZone uses the same source as the User Profile / User
 * Management page: getTimeZoneOptions() from utils/timezone.
 * The option value AND label are the raw IANA name, exactly
 * like the profile dropdown.
 * ============================================================
 */
const CONFIG_FIELDS = {
  calendly: [
    { key: 'bookingUrl',      label: 'Booking URL *',      placeholder: 'https://calendly.com/your-org/your-event', required: true },
    { key: 'apiToken',        label: 'API Token',          placeholder: 'Personal access token from Calendly', secret: true },
    { key: 'signingKey',      label: 'Signing Key',        placeholder: 'Webhook signing key', secret: true },
    { key: 'organizationUri', label: 'Organization URI',   placeholder: 'https://api.calendly.com/organizations/...' },
    { key: 'userUri',         label: 'User URI',           placeholder: 'https://api.calendly.com/users/...' },
  ],
  microsoft: [
    { key: 'bookingUrl',         label: 'Booking URL / Business ID *', placeholder: 'Bookings business ID or URL', required: true },
    { key: 'tenantId',           label: 'Tenant ID',                   placeholder: 'Azure AD tenant GUID' },
    { key: 'clientId',           label: 'Client ID',                   placeholder: 'App registration client ID' },
    { key: 'clientSecret',       label: 'Client Secret',               placeholder: 'App registration client secret', secret: true },
    { key: 'timeZone',           label: 'Microsoft Time Zone',         type: 'select', optionsFrom: 'timezones' },
    { key: 'webhookClientState', label: 'Webhook Client State',        placeholder: 'Shared secret echoed by Graph' },
  ],
};

const empty = {
  name: '',
  provider: 'calendly',
  leadSources: [],
  isActive: true,
  config: {},
};

const extractLeadSources = (response) => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.leadSources)) return response.leadSources;
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.items)) return response.items;
  return [];
};

/* Resolves the option list for a select field at render time. */
function resolveFieldOptions(field) {
  if (field.optionsFrom === 'timezones') {
    try {
      const list = getTimeZoneOptions();
      return Array.isArray(list) ? list : [];
    } catch {
      return [];
    }
  }
  return Array.isArray(field.options) ? field.options : [];
}

function buildConfigPayload(provider, rawConfig) {
  const fields = CONFIG_FIELDS[provider] || [];
  const out = {};
  for (const f of fields) {
    const raw = rawConfig?.[f.key];
    const trimmed = raw === undefined || raw === null ? '' : String(raw).trim();
    out[f.key] = trimmed === '' ? null : trimmed;
  }
  return out;
}

function configFromApp(provider, app) {
  const fields = CONFIG_FIELDS[provider] || [];
  const src = app?.config || {};
  const out = {};

  for (const f of fields) {
    let v = src[f.key];

    if (
      f.key === 'bookingUrl' &&
      (v === undefined || v === null || String(v).trim() === '')
    ) {
      v = app?.bookingUrl || '';
    }

    out[f.key] = v === undefined || v === null ? '' : String(v);
  }
  return out;
}

export default function BookingAppManagement() {
  const [apps, setApps] = useState([]);
  const [leadSources, setLeadSources] = useState([]);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [modalError, setModalError] = useState('');

  const [showSecrets, setShowSecrets] = useState({});

  const load = async () => {
    setLoading(true);
    setError('');

    try {
      const [appsResponse, leadSourceResponse] = await Promise.all([
        api.getAdminBookingApps(),
        api.getMasterData('leadSources'),
      ]);

      setApps(Array.isArray(appsResponse) ? appsResponse : []);

      const sources = extractLeadSources(leadSourceResponse)
        .filter((item) => item && (item.name || item.value))
        .map((item) => ({ ...item, name: item.name || item.value }));

      setLeadSources(sources);
    } catch (e) {
      console.error('Failed to load booking apps/master data:', e);
      setError(
        e.response?.data?.error ||
          'Failed to load booking apps and Lead Source master data',
      );
      setApps([]);
      setLeadSources([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setModalError('');

    const config = buildConfigPayload(form.provider, form.config);
    const bookingUrl = config.bookingUrl || '';

    const missing = [];
    if (!String(form.name || '').trim()) missing.push('Name');
    if (!bookingUrl) missing.push('Booking URL');

    if (missing.length) {
      setModalError(`Please fill in: ${missing.join(', ')}`);
      return;
    }

    setSaving(true);

    try {
      const payload = {
        name: form.name,
        provider: form.provider,
        bookingUrl,
        leadSources: form.leadSources,
        isActive: form.isActive,
        config,
      };

      if (editing) {
        await api.updateBookingApp(editing.id, payload);
      } else {
        await api.createBookingApp(payload);
      }

      setShow(false);
      setEditing(null);
      setShowSecrets({});
      setModalError('');
      setForm({ ...empty, leadSources: [] });

      await load();
    } catch (e) {
      console.error('Failed to save booking app:', e);
      setModalError(e.response?.data?.error || 'Failed to save booking app');
    } finally {
      setSaving(false);
    }
  };

  const edit = (app) => {
    const provider = app.provider || 'calendly';

    setEditing(app);
    setForm({
      name: app.name || '',
      provider,
      leadSources: Array.isArray(app.leadSources) ? app.leadSources : [],
      isActive: Boolean(app.isActive),
      config: configFromApp(provider, app),
    });

    setShowSecrets({});
    setModalError('');
    setShow(true);
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this booking app?')) return;

    try {
      await api.deleteBookingApp(id);
      await load();
    } catch (e) {
      console.error('Failed to delete booking app:', e);
      setError(e.response?.data?.error || 'Failed to delete booking app');
    }
  };

  const openAdd = () => {
    setEditing(null);
    setForm({
      ...empty,
      leadSources: [],
      config: configFromApp('calendly', { config: {} }),
    });
    setShowSecrets({});
    setModalError('');
    setShow(true);
  };

  const toggleLeadSource = (sourceName) => {
    setForm((current) => {
      const currentSources = Array.isArray(current.leadSources)
        ? current.leadSources
        : [];
      const exists = currentSources.includes(sourceName);

      return {
        ...current,
        leadSources: exists
          ? currentSources.filter((s) => s !== sourceName)
          : [...currentSources, sourceName],
      };
    });
  };

  const updateConfigField = (key, value) => {
    setForm((current) => ({
      ...current,
      config: { ...(current.config || {}), [key]: value },
    }));
  };

  const changeProvider = (newProvider) => {
    setForm((current) => ({
      ...current,
      provider: newProvider,
      config: configFromApp(newProvider, { config: {} }),
    }));
    setShowSecrets({});
  };

  const toggleSecret = (key) => {
    setShowSecrets((s) => ({ ...s, [key]: !s[key] }));
  };

  if (loading) {
    return <LoadingSpinner message="Loading booking apps..." />;
  }

  const providerFields = CONFIG_FIELDS[form.provider] || [];

  return (
    <div>
      <div className="management-header">
        <div>
          <h2>Booking Apps</h2>
          <p className="text-muted">
            Configure one or multiple demo booking apps and map them to Lead Source.
          </p>
        </div>

        <button type="button" className="btn btn-primary" onClick={openAdd}>
          <i className="fas fa-plus" /> Add Booking App
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      {show && (
        <div className="modal-overlay">
          <div className="modal">
            <h2 style={{ marginBottom: 16 }}>
              {editing ? 'Edit Booking App' : 'Add Booking App'}
            </h2>

            {modalError && (
              <div className="error-message" style={{ marginBottom: 12 }}>
                {modalError}
              </div>
            )}

            <form onSubmit={submit}>
              <div className="form-group">
                <label>Name *</label>
                <input
                  className="form-control"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                />
              </div>

              <div className="form-group">
                <label>Provider *</label>
                <select
                  className="form-control"
                  value={form.provider}
                  onChange={(e) => changeProvider(e.target.value)}
                >
                  <option value="calendly">Calendly</option>
                  <option value="microsoft">Microsoft Bookings</option>
                </select>
              </div>

              <div className="config-section">
                <div className="config-section-header">
                  <strong>
                    {form.provider === 'calendly'
                      ? 'Calendly Configuration'
                      : 'Microsoft Graph Configuration'}
                  </strong>
                  <span className="config-section-hint">
                    Leave blank to store null
                  </span>
                </div>

                {providerFields.map((field) => {
                  const options =
                    field.type === 'select' ? resolveFieldOptions(field) : [];

                  return (
                    <div className="form-group" key={field.key}>
                      <label>{field.label}</label>

                      {field.type === 'select' ? (
                        <select
                          className="form-control"
                          value={form.config?.[field.key] || ''}
                          onChange={(e) =>
                            updateConfigField(field.key, e.target.value)
                          }
                        >
                          <option value="">— Select —</option>
                          {options.map((tz) => (
                            <option key={tz} value={tz}>
                              {tz}
                            </option>
                          ))}
                        </select>
                      ) : field.secret ? (
                        <div className="secret-input-wrapper">
                          <input
                            className="form-control"
                            type={showSecrets[field.key] ? 'text' : 'password'}
                            value={form.config?.[field.key] || ''}
                            placeholder={field.placeholder}
                            autoComplete="new-password"
                            onChange={(e) =>
                              updateConfigField(field.key, e.target.value)
                            }
                          />
                          <button
                            type="button"
                            className="btn-icon secret-toggle"
                            title={showSecrets[field.key] ? 'Hide' : 'Show'}
                            onClick={() => toggleSecret(field.key)}
                          >
                            <i
                              className={`fas ${
                                showSecrets[field.key]
                                  ? 'fa-eye-slash'
                                  : 'fa-eye'
                              }`}
                            />
                          </button>
                        </div>
                      ) : (
                        <input
                          className="form-control"
                          type="text"
                          value={form.config?.[field.key] || ''}
                          placeholder={field.placeholder}
                          required={field.required === true}
                          onChange={(e) =>
                            updateConfigField(field.key, e.target.value)
                          }
                        />
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="form-group">
                <label>Lead Sources</label>
                {leadSources.length === 0 ? (
                  <div className="text-muted">
                    No Lead Sources are configured in the master data.
                  </div>
                ) : (
                  <div>
                    {leadSources.map((source) => {
                      const sourceName = source.name;
                      return (
                        <label
                          key={source.id || sourceName}
                          className="lead-source-item"
                        >
                          <input
                            type="checkbox"
                            checked={form.leadSources.includes(sourceName)}
                            onChange={() => toggleLeadSource(sourceName)}
                          />
                          {sourceName}
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="form-group">
                <label>Active</label>
                <select
                  className="form-control"
                  value={form.isActive ? '1' : '0'}
                  onChange={(e) =>
                    setForm({ ...form, isActive: e.target.value === '1' })
                  }
                >
                  <option value="1">Active</option>
                  <option value="0">Inactive</option>
                </select>
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShow(false);
                    setEditing(null);
                    setShowSecrets({});
                    setModalError('');
                  }}
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={saving || leadSources.length === 0}
                >
                  {saving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="table-wrapper">
        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Provider</th>
              <th>Lead Sources</th>
              <th>URL</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>

          <tbody>
            {apps.map((app) => (
              <tr key={app.id}>
                <td>
                  <strong>{app.name}</strong>
                </td>
                <td>
                  {app.provider === 'calendly'
                    ? 'Calendly'
                    : 'Microsoft Bookings'}
                </td>
                <td>
                  {Array.isArray(app.leadSources) && app.leadSources.length > 0
                    ? app.leadSources.join(', ')
                    : '—'}
                </td>
                <td>{app.config?.bookingUrl || app.bookingUrl || '—'}</td>
                <td>{app.isActive ? 'Active' : 'Inactive'}</td>
                <td className="actions">
                  <button
                    type="button"
                    className="btn btn-sm btn-secondary"
                    onClick={() => edit(app)}
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-danger"
                    onClick={() => remove(app.id)}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}

            {apps.length === 0 && (
              <tr>
                <td colSpan="6" style={{ textAlign: 'center' }}>
                  No booking apps configured.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}